import React, { useContext, useEffect, useRef, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import {
  ROUTE_SESSIONS_ENTITY_SESSION_PERSON,
  ROUTE_SESSIONS_ENTITY_SESSION_PERSONS
} from 'routes/RouteList';
import messages from 'messages';
import {
  SessionContextProvider,
  useSessionContext
} from 'contexts/SessionContext';
import { sortSportersOnFinishedAndDate } from 'utils/sort';
import useSelectableObject from 'hooks/utils/useSelectableObject';

import EntityListItem from 'containers/partials/list-items/EntityListItem';
import RecurringTestDataRow from 'containers/pages/sessions/RecurringTestDataRow';
import EntityList from 'containers/pages/persons/EntityList';
import { getLocalDateFormat } from 'utils/date';
import { ListItemLabel } from 'components/list/list-item';
import differenceInWeeks from 'date-fns/differenceInWeeks';
import { differenceInCalendarDays } from 'date-fns';
import ContextMenuProvider, {
  useContextMenu
} from 'contexts/ContextMenuProvider';
import { StoreContext } from 'index';
import { Button, ButtonExpandable } from 'components/button';
import { FormattedMessage, useIntl } from 'react-intl';
import slugify from 'slugify';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import Icon from 'components/icon/Icon';
import RemoveSporterFromSessionButton from 'containers/partials/buttons/RemoveSporterFromSessionButton';
import {
  createEntityStore,
  EntityStoreProvider
} from 'containers/pages/persons/store/EntityStoreContext';
import AssignPersonsToSessionModal from 'containers/pages/sessions/AssignPersonsToSessionModal';
import GenerateQRCodeModal from 'containers/partials/modals/GenerateQRCodeModal';
import { MenuItemsGroup, MenuItemsGroupItem } from 'components/menu';
import DownloadReportsButton from 'containers/partials/session-report-buttons/DownloadReportsButton';
import DownloadReportOptionsModal from 'containers/partials/modals/DownloadReportOptionsModal';

const SessionPersonsTab = ({ onSelectPersons }) => {
  const {
    authStore: { user }
  } = useContext(StoreContext);
  const intl = useIntl();
  const { push } = useHistory();
  const { entityId, sessionsTab, sessionId, personId } = useParams();
  const ability = useAbility(AbilityContext);
  const entityStore = useRef(createEntityStore()).current;
  const [sortedPersons, setSortedPersons] = useState([]);
  const { session } = useSessionContext();
  const { selected, select, clearSelected } = useSelectableObject();
  const [byIndex, setByIndex] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showAddPersonsModal, setShowAddPersonsModal] = useState(null);
  const [showReportOptionsModal, setShowReportOptionsModal] = useState(false);

  const { setMenuItems, addActionButton, clearAllContext } = useContextMenu();

  const AddPersonsButton = props =>
    props.expandable ? (
      <ButtonExpandable
        onClick={() => setShowAddPersonsModal(true)}
        dataQa={`${slugify(
          messages.buttonSessionAddSporters.id.toLowerCase()
        )}-expandable`}
        tabIndex={0}
        label={intl.formatMessage(messages.buttonSessionAddSporters)}
      />
    ) : (
      <Button
        onClick={() => setShowAddPersonsModal(true)}
        dataQa={`${slugify(
          messages.buttonSessionAddSporters.id.toLowerCase()
        )}-menu`}
        {...props}
      >
        <Icon id={props?.menu ? 'add' : 'add-white'} />
        <FormattedMessage {...messages.buttonSessionAddSporters} />
      </Button>
    );

  const GenerateQRCodeButton = () => (
    <Button
      onClick={() => setShowQrModal(true)}
      menu
      disabled={selected.length === 0}
      dataQa={`${slugify(
        messages.menuItemSessionGenerateQRCodes.id.toLowerCase()
      )}-menu`}
      title={
        selected.length <= 0
          ? intl.formatMessage(messages.menuItemNoSelectedPersonsTooltip)
          : null
      }
    >
      <FormattedMessage {...messages.generateQrCode} />
      <Icon id="qr-code" />
    </Button>
  );

  useEffect(() => {
    const contextButtons = [];

    if (ability.can('manage', 'Sessions')) {
      contextButtons.push(<AddPersonsButton menu />);
    }

    if (ability.can('manage', 'Sessions') && !user.isPerson()) {
      contextButtons.push(
        <RemoveSporterFromSessionButton
          menu
          ids={selected.map(sporter => sporter.id)}
          sessionId={session.id}
          rootEntityId={entityId}
          onComplete={() => {
            clearSelected();
            push(
              generatePath(ROUTE_SESSIONS_ENTITY_SESSION_PERSONS, {
                entityId,
                sessionsTab: 'sessions',
                sessionId: session.id
              })
            );
          }}
          disabled={selected.length === 0}
          dataQa={`${slugify(
            messages.menuItemSessionRemoveSporters.id.toLowerCase()
          )}-menu`}
        />
      );
    }

    if (session.finishedPercentage !== 0 && !session.isRecurring) {
      contextButtons.push(
        <DownloadReportsButton
          type={'group'}
          modalCallback={callBack => setShowReportOptionsModal(callBack)}
        />
      );
    }

    if (ability.can('manage', 'AutomatedMeasurementProgram')) {
      contextButtons.push(
        <MenuItemsGroup key={'menuItemsGroup'}>
          <MenuItemsGroupItem>
            <GenerateQRCodeButton />
          </MenuItemsGroupItem>
        </MenuItemsGroup>
      );
    }

    addActionButton(<AddPersonsButton expandable />);

    setMenuItems(contextButtons);
    return () => {
      clearAllContext();
    };
  }, [sortedPersons, selected]);

  useEffect(() => {
    if (session?.sporters) {
      setSortedPersons(sortSportersOnFinishedAndDate(session.sporters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, sortSportersOnFinishedAndDate]);

  const onPersonClickHandler = person => {
    if (personId !== person.id)
      push(
        generatePath(ROUTE_SESSIONS_ENTITY_SESSION_PERSON, {
          entityId,
          sessionsTab,
          sessionId,
          personId: person.id
        })
      );
  };

  const onSelectPersonsHandler = person => {
    const selectedPersons = select(person);
    onSelectPersons && onSelectPersons(selectedPersons);
  };

  const findLastTestDate = personId => {
    const lastTestData = session.getLastTestData(personId, true);
    if (lastTestData) {
      return getLocalDateFormat(lastTestData.testDataDate);
    }
    return null;
  };

  const calculateTestProgress = personId => {
    let total = 0;
    if (session.interval === 'weekly') {
      total = differenceInWeeks(session.enddate, session.startdate, {
        roundingMethod: 'ceil'
      });
    }
    if (session.interval === 'daily') {
      total = differenceInCalendarDays(session.enddate, session.startdate) + 1;
    }
    const testData = session.getTestDataByPersonId(personId, true);
    if (testData) {
      return [testData.length, total];
    }
    return [0, total];
  };

  const openExtraRow = index => {
    setByIndex(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <>
      <EntityList
        activeId={personId}
        variableHeight
        reCalculateHeight={selected.length > 0}
        entityId={entityId}
        items={sortedPersons}
        selectedItems={selected}
        statusLabel={item => {
          if (session.recurring) {
            const progress = calculateTestProgress(item.id);
            return `${progress[0]}/${progress[1]}`;
          } else {
            return `${item.finished}%`;
          }
        }}
        checkable
        onClick={onPersonClickHandler}
        onSelect={onSelectPersonsHandler}
        emptyMessage={messages.labelSessionNoSporters}
        extraClassNames="c-list--medium-negative-margin"
      >
        {item => {
          let itemData = {
            done: item.finished === 100,
            inProgress: item.finished !== 0 && item.finished !== 100
          };
          if (session.isRecurring) {
            const progress = calculateTestProgress(item.id);
            itemData = {
              done: progress[0] >= progress[1],
              inProgress: progress[0] > 0 && progress[0] < progress[1],
              meta: () => (
                <>
                  <ListItemLabel info>
                    {findLastTestDate(item.id)}
                  </ListItemLabel>
                </>
              )
            };
          }
          return (
            <EntityListItem
              id={item.id}
              active={personId === item.id}
              /*meta={props =>  // TODO @Maarten: recurring sessions lijst activeren
              session.recurring ? (
                <MetaWrapper style={{ bottom: 'calc(100% - 48px)' }} {...props}>
                  <Button onClick={() => openExtraRow(item.index)}>
                    {byIndex[item.index] ? 'close' : 'open'}
                  </Button>
                </MetaWrapper>
              ) : (
                false
              )
            }*/

              extraRow={() =>
                byIndex[item.index] ? <RecurringTestDataRow /> : false
              }
              {...itemData}
            />
          );
        }}
      </EntityList>

      {showAddPersonsModal && (
        <ContextMenuProvider>
          <SessionContextProvider>
            <EntityStoreProvider value={entityStore}>
              <AssignPersonsToSessionModal
                entityId={entityId}
                onClose={() => {
                  setShowAddPersonsModal(null);
                }}
                onDone={() => {
                  setShowAddPersonsModal(null);
                }}
              />
            </EntityStoreProvider>
          </SessionContextProvider>
        </ContextMenuProvider>
      )}

      {showReportOptionsModal && (
        <DownloadReportOptionsModal
          callback={showReportOptionsModal}
          onClose={() => setShowReportOptionsModal(false)}
        />
      )}

      {showQrModal && (
        <GenerateQRCodeModal
          onClose={() => setShowQrModal(false)}
          persons={selected}
          type="scanner"
          entityId={entityId}
        />
      )}
    </>
  );
};

export default SessionPersonsTab;
